import {matchUnifiedHeader} from './apply-patch-parser'

/**
 * A tool result covers everything one call did, so a call that wrote several
 * files reports several diffs. These helpers narrow it to one path.
 */

const GIT_HEADER = /^diff --git\s/
/** Best-effort path from a `diff --git` line; ambiguous when the path has spaces. */
const GIT_HEADER_PATH = /^diff --git\s+(?:"(?:[^"\\]|\\.)*"|\S+)\s+(?:"((?:[^"\\]|\\.)*)"|(\S+))\s*$/
const ENVELOPE_HEADER = /^\*\*\*\s+(?:Add|Update|Delete)\s+File:(.*)$/
const MOVE_HEADER = /^\*\*\*\s+Move\s+to:(.*)$/
const HUNK_HEADER = /^@@ -\d+(?:,(\d+))? \+\d+(?:,(\d+))? @@/
/** Envelope hunks carry no counts, so their body runs to the next boundary. */
const BARE_HUNK_HEADER = /^@@(?:\s|$)/

/** Linear trailing line-ending trim; a `/[\r\n]+$/` regex backtracks on long runs. */
function trimTrailingNewlines(text: string): string {
  let end = text.length
  // Splitting on `\n` leaves each CRLF line's `\r` at its end, so a section can
  // finish on a bare `\r` that is still a line ending, not content.
  while (end > 0 && (text[end - 1] === '\n' || text[end - 1] === '\r')) end--
  return text.slice(0, end)
}

/**
 * Tool paths may or may not be rooted. The `a/`/`b/` strip that diff headers
 * need is deliberately not applied: a real first directory named `a` would
 * otherwise collide with a sibling at the root.
 */
function normalizeToolPath(path: string): string {
  return path.replace(/^\/+/, '')
}

interface Section {
  path: string
  lines: string[]
  /** Set once this section has consumed its `---` line. */
  sawOldHeader: boolean
  /** Set once its `+++` line has supplied the final path. */
  sawNewHeader: boolean
  /** Envelope sections use bare `@@` hunks that run to the next boundary. */
  fromEnvelope: boolean
}

/**
 * The sections of `preview` describing `path`, or `undefined` when it has file
 * sections and none match. Text with no sections is returned unchanged, since a
 * tool reporting prose is still describing its own write.
 *
 * Paths come from the `---`/`+++` headers rather than the `diff --git` line,
 * which repeats an unquoted path twice and so cannot be split when it contains
 * a space.
 */
export function previewForPath(preview: string, path: string): string | undefined {
  const sections: Section[] = []
  const preamble: string[] = []
  let current: Section | undefined
  // Hunk bodies are scanned by line count, because their content can look like
  // a header: removing the text `-- option` is encoded as `--- option`.
  let inBareHunk = false
  let oldRemaining = 0
  let newRemaining = 0

  const open = (path_: string, line: string, sawOldHeader = false, fromEnvelope = false): Section => {
    current = {path: path_, lines: [line], sawOldHeader, sawNewHeader: false, fromEnvelope}
    sections.push(current)
    return current
  }

  const lines = preview.split('\n')
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? ''
    // Only a `---` immediately followed by `+++` opens a file. Hunk content can
    // look like a header on its own: removing `-- option` is encoded `--- option`.
    const opensPair =
      matchUnifiedHeader(line)?.kind === 'old' && matchUnifiedHeader(lines[index + 1] ?? '')?.kind === 'new'
    // A hunk body always marks its lines, so an unmarked file header ends the
    // hunk even when its declared counts overrun.
    // Counts win over the pair rule: a hunk can legitimately contain the pair
    // as content, since replacing `-- old` with `++ new` encodes as `--- old`
    // then `+++ new`. Unmarked headers still break out, as content is marked.
    const inCountedHunk = oldRemaining > 0 || newRemaining > 0 || inBareHunk
    const startsFile = GIT_HEADER.test(line) || ENVELOPE_HEADER.test(line) || (opensPair && !inCountedHunk)
    if (!startsFile && (oldRemaining > 0 || newRemaining > 0)) {
      const marker = line[0]
      if (marker === '-') oldRemaining--
      else if (marker === '+') newRemaining--
      else {
        oldRemaining--
        newRemaining--
      }
      current?.lines.push(line)
      continue
    }

    oldRemaining = 0
    newRemaining = 0
    if (startsFile) inBareHunk = false

    const hunk = HUNK_HEADER.exec(line)
    if (hunk === null && BARE_HUNK_HEADER.test(line)) {
      // No counts to consume, so this body ends only at the next boundary.
      inBareHunk = current?.fromEnvelope === true
      current?.lines.push(line)
      continue
    }
    if (hunk) {
      oldRemaining = Number(hunk[1] ?? 1)
      newRemaining = Number(hunk[2] ?? 1)
      current?.lines.push(line)
      continue
    }

    const envelope = ENVELOPE_HEADER.exec(line)
    if (envelope) {
      open(normalizeToolPath(envelope[1]?.trim() ?? ''), line, true, true)
      continue
    }

    const move = MOVE_HEADER.exec(line)
    if (move && current) {
      // The artifact is keyed by where the file ended up.
      current.path = normalizeToolPath(move[1]?.trim() ?? current.path)
      current.lines.push(line)
      continue
    }

    if (GIT_HEADER.test(line)) {
      const named = GIT_HEADER_PATH.exec(line)
      const quoted = named?.[1]
      const target = quoted === undefined ? named?.[2] : (matchUnifiedHeader(`--- "${quoted}"`)?.path ?? quoted)
      open(target === undefined ? '' : normalizeToolPath(target.replace(/^[ab]\//, '')), line)
      continue
    }

    const header = matchUnifiedHeader(line)
    if (header?.kind === 'old' && opensPair && !inCountedHunk) {
      // A `diff --git` already opened this section; otherwise this line does.
      const section = current !== undefined && !current.sawOldHeader ? current : open('', line, true)
      section.sawOldHeader = true
      if (section.lines.at(-1) !== line) section.lines.push(line)
      // The old path stands in until `+++` supplies the new one, so a deletion
      // still resolves to the file it removed.
      if (section.path === '' && header.path !== undefined) section.path = normalizeToolPath(header.path)
      continue
    }
    if (
      header?.kind === 'new' &&
      !inCountedHunk &&
      current !== undefined &&
      current.sawOldHeader &&
      !current.sawNewHeader
    ) {
      current.sawNewHeader = true
      if (header.path !== undefined) current.path = normalizeToolPath(header.path)
      current.lines.push(line)
      continue
    }

    if (current === undefined) preamble.push(line)
    else current.lines.push(line)
  }

  if (sections.length === 0) return preview

  const wanted = normalizeToolPath(path)
  const matching = sections.filter(section => section.path === wanted)
  if (matching.length === 0) return undefined

  // Only separator newlines go; trailing spaces can be significant, as in a
  // Markdown hard break. Any lead-in prose describes the call, not one file.
  const lead = trimTrailingNewlines(preamble.join('\n'))
  const body = matching.map(section => trimTrailingNewlines(section.lines.join('\n'))).join('\n\n')
  return lead ? `${lead}\n\n${body}` : body
}
